/* Shop4Seats — global JS (every page, deferred, no framework).
 * S4S.money, S4S.cart (add/change + drawer refresh), <s4s-header> (mega menu, mobile menu),
 * <s4s-search> (popular + predictive), wishlist (localStorage), modal helpers. */
(() => {
  const S4S = (window.S4S = window.S4S || {});

  /* ---------- Money ---------- */
  S4S.money = (cents) => {
    const fmt = S4S.moneyFormat || '${{amount}}';
    const n = Number(cents) / 100;
    const withSep = (d, sep = ',', dec = '.') => {
      const [i, f] = n.toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (d ? dec + f : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: withSep(2), amount_no_decimals: withSep(0),
      amount_with_comma_separator: withSep(2, '.', ','), amount_no_decimals_with_comma_separator: withSep(0, '.'),
    }[k] || withSep(2)));
  };

  /* ---------- Button loading state (.is-loading in base.css) ---------- */
  S4S.busy = (el, on) => {
    if (!el) return;
    el.classList.toggle('is-loading', on);
    if (on) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
    if ('disabled' in el) el.disabled = on;
  };
  // Native form posts (contact, newsletter, account, checkout…): spinner on the submit button while the page loads.
  // Forms handled in JS call preventDefault first and are skipped; bfcache restores reset the button.
  document.addEventListener('submit', (e) => {
    if (e.target.target && e.target.target !== '_self') return; // opens elsewhere: this page never unloads
    const b = e.submitter || e.target.querySelector('[type="submit"]');
    setTimeout(() => { if (!e.defaultPrevented && b && b.matches('button, input[type="submit"]')) S4S.busy(b, true); });
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) document.querySelectorAll('.is-loading').forEach((el) => S4S.busy(el, false));
  });

  /* ---------- Body scroll lock (drawers / modals) ---------- */
  let locks = 0;
  S4S.lock = (on) => {
    locks = Math.max(0, locks + (on ? 1 : -1));
    document.body.classList.toggle('is-locked', locks > 0);
  };

  /* ---------- Cart ---------- */
  // Sections re-rendered with every cart change: the drawer + any section on the page marked [data-cart-section]
  const cartSections = () => ['cart-drawer', ...[...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection)];
  // Products in the cart: top-level lines only — nested upgrades/add-ons don't count (snippets/cart-count)
  const countOf = (cart) => (cart.items || [])
    .filter((i) => !i.parent_relationship && !(i.properties && i.properties._group))
    .reduce((n, i) => n + i.quantity, 0);
  const setCount = (n) => document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = n;
    el.hidden = !n;
  });
  const renderSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      const el = document.getElementById(`shopify-section-${id}`);
      if (!el || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      // Swap only the [data-render] part so open/closed state of the wrapper survives
      const target = el.querySelector('[data-render]') || el;
      const fresh = doc.querySelector('[data-render]') || doc.getElementById(`shopify-section-${id}`);
      if (fresh) target.replaceChildren(...fresh.childNodes);
    });
  };
  async function post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: cartSections().join(','), sections_url: location.pathname }),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Cart error');
    return json;
  }
  async function refreshCount() {
    const c = await fetch(`${S4S.routes.cart}.js`).then((r) => r.json());
    setCount(countOf(c));
    return c;
  }
  const changed = () => document.dispatchEvent(new CustomEvent('s4s:cart-updated'));
  S4S.cart = {
    async add(items) {
      const json = await post(`${S4S.routes.cartAdd}.js`, { items });
      renderSections(json.sections);
      await refreshCount();
      changed();
      return json;
    },
    // line: 1-based index, or a line item key (string) — keys stay valid for nested child lines
    async change(line, quantity) {
      const json = await post(`${S4S.routes.cartChange}.js`, typeof line === 'string' ? { id: line, quantity } : { line, quantity });
      renderSections(json.sections);
      setCount(countOf(json));
      changed();
      return json;
    },
    async update(updates) {
      const json = await post(`${S4S.routes.cartUpdate}.js`, { updates });
      renderSections(json.sections);
      setCount(countOf(json));
      changed();
      return json;
    },
    open() { document.querySelector('s4s-cart-drawer')?.open(); },
  };

  /* Shared cart controls (drawer + cart page):
   *   [data-line-key|data-line][data-line-qty]  set a line quantity (0 = remove); + data-line-kids scales nested lines
   *   input[data-white-glove]                   add / remove the white-glove line
   *   [data-cart-add][data-variant-id]          add a product (cart page "Finish the install") */
  class CartControls extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => {
        const wg = e.target.closest('[data-white-glove]');
        if (!wg) return;
        const line = wg.dataset.lineKey || Number(wg.dataset.line || 0);
        this.busy(wg.checked ? S4S.cart.add([{ id: Number(wg.dataset.variantId), quantity: 1 }]) : S4S.cart.change(line, 0));
      });
    }
    onClick(e) {
      const btn = e.target.closest('[data-line-qty]');
      if (btn) {
        e.preventDefault();
        const qty = Math.max(0, Number(btn.dataset.lineQty));
        // A configured seat scales its nested lines with it (data-line-kids="key:per-unit,…", snippets/cart-qty)
        if (btn.dataset.lineKids && qty > 0) {
          const updates = { [btn.dataset.lineKey]: qty };
          btn.dataset.lineKids.split(',').filter(Boolean).forEach((kv) => {
            const i = kv.lastIndexOf(':');
            updates[kv.slice(0, i)] = Number(kv.slice(i + 1)) * qty;
          });
          return this.busy(S4S.cart.update(updates), btn);
        }
        return this.busy(S4S.cart.change(btn.dataset.lineKey || Number(btn.dataset.line), qty), btn);
      }
      const add = e.target.closest('[data-cart-add]');
      if (add) this.busy(S4S.cart.add([{ id: Number(add.dataset.variantId), quantity: 1 }]), add);
    }
    busy(p, btn) {
      this.setAttribute('aria-busy', 'true');
      S4S.busy(btn, true);
      // the button is usually replaced by the re-render; reset it in case it is not
      p.catch((err) => console.error(err)).finally(() => { this.removeAttribute('aria-busy'); if (btn && btn.isConnected) S4S.busy(btn, false); });
    }
  }
  customElements.define('s4s-cart-controls', class extends CartControls {});

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends CartControls {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (!opener) return;
        // On the cart page itself the icon simply links to it
        if (document.querySelector('[data-cart-page]')) return;
        e.preventDefault();
        this.open(opener);
      });
    }
    onClick(e) {
      if (e.target.closest('[data-cart-close]')) return this.close();
      super.onClick(e);
    }
    get panel() { return this.querySelector('[data-cart-panel]'); }
    open(opener) {
      if (document.querySelector('[data-cart-page]')) return;
      this.opener = opener || document.activeElement;
      this.hidden = false;
      S4S.lock(true);
      requestAnimationFrame(() => this.querySelector('[data-cart-close]')?.focus());
    }
    close() {
      if (this.hidden) return;
      this.hidden = true;
      S4S.lock(false);
      this.opener?.focus?.();
    }
  }
  customElements.define('s4s-cart-drawer', CartDrawer);

  /* ---------- Header: mega menu + mobile menu ---------- */
  class Header extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('[data-mega-nav]');
      this.current = null;
      const fine = matchMedia('(hover: hover) and (pointer: fine)');
      this.querySelectorAll('[data-mega-trigger]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggle(btn.dataset.megaTrigger));
        btn.addEventListener('mouseenter', () => { if (fine.matches) this.toggle(btn.dataset.megaTrigger, true); });
      });
      this.nav?.addEventListener('mouseleave', () => { if (fine.matches) this.toggle(null); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.toggle(null); this.menu(false); } });
      document.addEventListener('click', (e) => { if (this.current && !this.nav.contains(e.target)) this.toggle(null); });

      this.menuBtn = this.querySelector('[data-menu-toggle]');
      this.menuPanel = this.querySelector('[data-menu-panel]');
      this.menuBtn?.addEventListener('click', () => this.menu(this.menuPanel.hidden));
      this.querySelectorAll('[data-menu-close]').forEach((b) => b.addEventListener('click', () => this.menu(false)));
    }
    toggle(id, open) {
      const next = open === true ? id : (this.current === id ? null : id);
      if (next === this.current) return;
      this.querySelectorAll('[data-mega-panel]').forEach((p) => { p.hidden = p.dataset.megaPanel !== next; });
      this.querySelectorAll('[data-mega-trigger]').forEach((b) => b.setAttribute('aria-expanded', String(b.dataset.megaTrigger === next)));
      this.current = next;
    }
    menu(open) {
      if (!this.menuPanel) return;
      if (open) {
        const bar = this.querySelector('.header__main');
        this.menuPanel.style.setProperty('--menu-top', `${Math.max(0, bar.getBoundingClientRect().bottom)}px`);
      }
      this.menuPanel.hidden = !open;
      this.menuBtn.setAttribute('aria-expanded', String(open));
      S4S.lock(open);
    }
  }
  customElements.define('s4s-header', Header);

  /* ---------- Search: popular searches on focus, predictive results while typing ---------- */
  class Search extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.panel = this.querySelector('.search__panel');
      this.results = this.querySelector('[data-search-results]');
      this.popular = this.querySelector('[data-search-popular]');
      let t;
      this.input.addEventListener('focus', () => this.open(true));
      this.input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => this.query(), 180); });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.open(false); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.open(false); this.input.blur(); } });
    }
    open(on) {
      this.panel.hidden = !on;
      this.classList.toggle('is-open', on);
      this.input.setAttribute('aria-expanded', String(on));
    }
    async query() {
      const q = this.input.value.trim();
      this.popular.hidden = !!q;
      if (!q) { this.results.replaceChildren(); return; }
      const url = `${S4S.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5&section_id=predictive-search`;
      try {
        const html = await fetch(url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.querySelector('[data-predictive]');
        if (this.input.value.trim() === q && body) this.results.replaceChildren(...body.childNodes);
      } catch (e) { /* network: keep popular list */ }
    }
  }
  customElements.define('s4s-search', Search);

  /* ---------- Wishlist (localStorage, handles) ---------- */
  const WKEY = 's4s_wishlist';
  const wishList = () => { try { return JSON.parse(localStorage.getItem(WKEY) || '[]'); } catch (e) { return []; } };
  const syncWish = () => {
    const list = wishList();
    document.querySelectorAll('[data-wish-toggle]').forEach((b) => b.setAttribute('aria-pressed', String(list.includes(b.dataset.wishToggle))));
    document.querySelectorAll('[data-wish-count]').forEach((el) => { el.textContent = list.length; el.hidden = !list.length; });
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-wish-toggle]');
    if (!b) return;
    e.preventDefault();
    const h = b.dataset.wishToggle;
    const list = wishList();
    const next = list.includes(h) ? list.filter((x) => x !== h) : list.concat(h);
    try { localStorage.setItem(WKEY, JSON.stringify(next)); } catch (err) { /* storage blocked */ }
    syncWish();
  });
  S4S.syncWish = syncWish;
  syncWish();

  /* ---------- Generic modal / drawer: [data-modal-open="id"], [data-modal-close] ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      const m = document.getElementById(opener.dataset.modalOpen);
      if (m && m.hidden) { m.hidden = false; m._opener = opener; S4S.lock(true); m.querySelector('[data-modal-close]')?.focus(); }
      return;
    }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) {
      const m = closer.closest('[data-modal]');
      if (m && !m.hidden) { m.hidden = true; S4S.lock(false); m._opener?.focus?.(); }
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-modal]:not([hidden])').forEach((m) => { m.hidden = true; S4S.lock(false); });
  });

  /* ---------- Tabs: [data-tabs] > [data-tab=n] buttons + [data-panel=n] panels ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-tab]');
    const box = t && t.closest('[data-tabs]');
    if (!box) return;
    const n = t.dataset.tab;
    box.querySelectorAll('[data-tab]').forEach((b) => b.setAttribute('aria-selected', b.dataset.tab === n ? 'true' : 'false'));
    box.querySelectorAll('[data-panel]').forEach((p) => { p.hidden = p.dataset.panel !== n; });
  });

  /* ---------- Mobile rails: tap a card to slide to the next one; optional progress bar ---------- */
  const mobile = matchMedia('(max-width: 989px)');
  document.addEventListener('click', (e) => {
    const rail = e.target.closest('[data-rail-advance]');
    if (!rail || !mobile.matches || e.target.closest('a, button')) return;
    const first = rail.firstElementChild;
    if (!first) return;
    const step = first.offsetWidth + parseFloat(getComputedStyle(rail).columnGap || 10);
    const max = rail.scrollWidth - rail.clientWidth;
    rail.scrollTo({ left: rail.scrollLeft + 2 >= max ? 0 : Math.min(max, rail.scrollLeft + step), behavior: 'smooth' });
  });
  const railBar = (rail) => {
    const bar = document.getElementById(rail.dataset.railProgress);
    if (!bar) return;
    const max = rail.scrollWidth - rail.clientWidth;
    const p = max > 0 ? rail.scrollLeft / max : 0;
    const w = rail.scrollWidth ? rail.clientWidth / rail.scrollWidth : 1;
    bar.style.setProperty('--bar-width', (w * 100).toFixed(2) + '%');
    bar.style.setProperty('--bar-left', (p * (1 - w) * 100).toFixed(2) + '%');
    bar.setAttribute('aria-valuenow', Math.round(p * 100));
  };
  document.addEventListener('scroll', (e) => { if (e.target.dataset && e.target.dataset.railProgress) railBar(e.target); }, { capture: true, passive: true });
  const initBars = () => document.querySelectorAll('[data-rail-progress]').forEach(railBar);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initBars); else initBars();
  mobile.addEventListener('change', initBars);
  document.addEventListener('shopify:section:load', initBars);

  /* ---------- Product card swatches: hover previews the colour's photo, click selects it (photo + links) ---------- */
  const cardShow = (card, b) => {
    const img = card.querySelector('.p-card__img');
    if (!img || !b || !b.dataset.swatchSrc) return;
    if (!card.dataset.baseSrc) { card.dataset.baseSrc = img.currentSrc || img.src; card.dataset.baseSrcset = img.srcset || ''; }
    img.srcset = b.dataset.swatchSrcset;
    img.src = b.dataset.swatchSrc;
  };
  const cardRestore = (card) => {
    const on = card.querySelector('.p-card__swatch[aria-pressed="true"]');
    if (on && on.dataset.swatchSrc) return cardShow(card, on);
    const img = card.querySelector('.p-card__img');
    if (img && card.dataset.baseSrc) { img.srcset = card.dataset.baseSrcset; img.src = card.dataset.baseSrc; }
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('.p-card__swatch');
    if (!b) return;
    const card = b.closest('.p-card');
    card.querySelectorAll('.p-card__swatch').forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
    cardShow(card, b);
    if (b.dataset.swatchUrl) card.querySelectorAll('.p-card__media > a, a.p-card__title').forEach((a) => { a.href = b.dataset.swatchUrl; });
  });
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const b = e.target.closest && e.target.closest('.p-card__swatch');
    if (b) cardShow(b.closest('.p-card'), b);
  }, { passive: true });
  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse') return;
    const group = e.target.closest && e.target.closest('.p-card__swatches');
    if (group && !group.contains(e.relatedTarget)) cardRestore(group.closest('.p-card'));
  }, { passive: true });

  /* ---------- Prefetch product/collection links on hover (fast navigations) ---------- */
  const seen = new Set();
  document.addEventListener('pointerover', (e) => {
    const a = e.target.closest('a[href^="/"]');
    if (!a || seen.has(a.href) || a.href.includes('#') || /\/(cart|checkout|account)/.test(a.pathname)) return;
    seen.add(a.href);
    const l = document.createElement('link');
    l.rel = 'prefetch';
    l.href = a.href;
    document.head.append(l);
  }, { passive: true });
})();
